import React from 'react';
import { SNAPASK_SITE_ENDPOINT } from 'config';
import Layout from 'components/Layout';

import { fetchCourse } from 'reduxModules/course/action';
import { fetchCourseFlow } from 'reduxModules/course/saga';

import { getCourseById } from 'reduxModules/course/selector';

import { fetchUpsellBanner } from 'reduxModules/upsellBanner/action';
import { fetchUpsellBannerFlow } from 'reduxModules/upsellBanner/saga';
import { COURSE } from 'constants/upsellBannerPage';

import CourseLesson from './CourseLesson';
import { generateOfficialSiteURL } from 'reduxModules/navigation/selector';
import {
  HOME,
  COURSE as COURSE_PAGE,
  COURSE_GROUP_TEMPLATE,
  COURSE_INFORMATION_TEMPLATE,
} from 'constants/routingPath';
import {
  createBreadcrumbForJsonLD,
  getOfferInfoForJsonLD,
  getRatingForJsonLD,
} from 'utils/jsonLD';
import { getContentCheckoutCollectionList } from 'reduxModules/checkoutCollection/selector';
import * as contentTypes from 'constants/contentTypes';
import { fetchCheckoutCollectionListFlow } from 'reduxModules/checkoutCollection/saga';
import { fetchCheckoutCollectionList } from 'reduxModules/checkoutCollection/action';

async function action(context) {
  const { store, params, pathname, i18n } = context;

  await Promise.all([
    store.runSagas(fetchCourseFlow, fetchCourse({ id: params.id })).toPromise(),
    store.runSagas(
      fetchCheckoutCollectionListFlow,
      fetchCheckoutCollectionList({
        contentType: contentTypes.COURSE,
        contentId: params.id,
      }),
    ),
    store
      .runSagas(fetchUpsellBannerFlow, fetchUpsellBanner({ page: COURSE }))
      .toPromise(),
  ]);

  const courseInfo = getCourseById(params.id)(store.getState());

  if (!courseInfo || !params.lessonId) {
    return undefined;
  }

  const contentList = getContentCheckoutCollectionList(
    contentTypes.COURSE,
    params.id,
  )(store.getState());

  const lessonId = +params.lessonId;

  const currentLessonInfo =
    courseInfo.lessonList.find(lesson => lesson.id === lessonId) ?? {};

  const metaTitle = currentLessonInfo.title || courseInfo.metaTitle;
  const metaDescription =
    currentLessonInfo.description || courseInfo.metaDescription;

  const pictureUrl = courseInfo.picture.originalUrl;

  const getOfficialSiteURL = generateOfficialSiteURL(store.getState());

  const breadcrumbList = [
    {
      link: getOfficialSiteURL(HOME),
      label: i18n.t('common:common.breadcrumb.home'),
    },
    {
      link: getOfficialSiteURL(COURSE_PAGE),
      label: i18n.t('navbar:navbar.link.course'),
    },
    {
      link: getOfficialSiteURL(COURSE_GROUP_TEMPLATE, {
        id: courseInfo.leadGroup.id,
      }),
      label: courseInfo.leadGroup.name,
    },
    {
      link: getOfficialSiteURL(COURSE_INFORMATION_TEMPLATE, {
        id: courseInfo.id,
      }),
      label: courseInfo.title,
    },
  ];

  const currentPage = {
    link: pathname,
    label: metaTitle,
  };

  return {
    title: metaTitle,
    description: metaDescription,
    ogTags: {
      title: metaTitle,
      description: metaDescription,
      image: pictureUrl,
      type: 'website',
      url: `${SNAPASK_SITE_ENDPOINT}${pathname}`,
    },
    jsonLDList: [
      {
        '@context': 'https://schema.org',
        '@type': 'Product',
        image: pictureUrl,
        name: metaTitle,
        description: metaDescription,
        publisher: {
          '@type': 'Organization',
          name: 'Snapask',
        },
        provider: {
          '@type': 'Person',
          name: courseInfo?.instructors[0]?.name,
          image: courseInfo?.instructors[0]?.avatarUrl,
        },
        ...getOfferInfoForJsonLD(courseInfo.checkoutCollection, contentList),
        ...getRatingForJsonLD(courseInfo.averageRate, courseInfo.ratings),
      },
      createBreadcrumbForJsonLD(
        [...breadcrumbList, currentPage],
        'course lesson page',
      ),
    ],
    chunks: ['courseLesson'],
    component: (
      <Layout>
        <CourseLesson
          id={params.id}
          lessonId={+params.lessonId}
          breadcrumbList={breadcrumbList}
        />
      </Layout>
    ),
  };
}

export default action;
